import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/context/AuthContext';
import { PageShell, StatCard, DataCard, StatusBadge, EmptyState } from '@/components/ds';
import { agentOnboardingAPI, agentApplicationAPI } from '@/lib/api';
import { OnboardingDetailDrawer } from '@/components/onboarding/OnboardingDetailDrawer';
import { ApplicationReviewDrawer } from '@/components/onboarding/ApplicationReviewDrawer';
import { isKRRole, getInitials } from '@/lib/utils';
import { Input } from '@/components/ui/input';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { toast } from 'sonner';
import {
  Workflow, Users, CheckCircle2, Clock, Search, ArrowRight, Loader2,
  AlertTriangle, Link2, Mail, ShieldCheck, ArrowUpRight, MailOpen, Send, UserPlus,
} from 'lucide-react';

const relativeTime = (iso) => {
  if (!iso) return '—';
  const diff = Date.now() - new Date(iso).getTime();
  const d = Math.floor(diff / (24 * 60 * 60 * 1000));
  if (d < 1) return 'today';
  if (d === 1) return 'yesterday';
  if (d < 30) return `${d}d ago`;
  return new Date(iso).toLocaleDateString();
};

const INVITE_BADGE = {
  pending: { label: 'Invite sent', variant: 'warning', icon: Mail },
  completed: { label: 'Profile completed', variant: 'success', icon: CheckCircle2 },
  expired: { label: 'Invite expired', variant: 'danger', icon: AlertTriangle },
  cancelled: { label: 'Invite cancelled', variant: 'neutral', icon: AlertTriangle },
};

function ChecklistBar({ checklist }) {
  const pct = checklist?.pct ?? 0;
  const done = pct === 100;
  return (
    <div>
      <div className="flex items-center justify-between mb-1.5">
        <span className="text-xs font-medium" style={{ color: 'var(--kr-navy-900)' }}>
          Onboarding checklist
        </span>
        <span className="text-xs tabular-nums font-semibold" style={{ color: done ? '#047857' : 'var(--kr-muted)' }}>
          {checklist?.completed ?? 0}/{checklist?.total ?? 0}
        </span>
      </div>
      <div className="h-2 rounded-full bg-slate-100 overflow-hidden">
        <div
          className="h-full rounded-full transition-all"
          style={{ width: `${pct}%`, background: done ? 'linear-gradient(90deg,#10b981,#059669)' : 'linear-gradient(90deg,var(--kr-navy-700),var(--kr-navy-900))' }}
        />
      </div>
      <div className="mt-2 flex flex-wrap gap-1.5">
        {(checklist?.items || []).map((it) => (
          <span
            key={it.key}
            className={`inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[10px] font-medium ${
              it.done ? 'bg-emerald-50 text-emerald-700 ring-1 ring-emerald-200' : 'bg-slate-50 text-slate-500 ring-1 ring-slate-200'
            }`}
            title={it.hint}
          >
            {it.done ? <CheckCircle2 size={10} /> : <Clock size={10} />}
            {it.label}
          </span>
        ))}
      </div>
    </div>
  );
}

function AgentCard({ agent, onOpen, onOpenProfile }) {
  const name = `${agent.first_name || ''} ${agent.last_name || ''}`.trim() || agent.email || 'New agent';
  const badge = INVITE_BADGE[agent.invite_status];
  const BadgeIcon = badge?.icon;
  const avatar = agent.photo_url ? (
    <img src={agent.photo_url} alt={name} className="w-10 h-10 rounded-full object-cover flex-shrink-0" />
  ) : (
    <div
      className="w-10 h-10 rounded-full flex items-center justify-center text-white text-sm font-semibold font-heading flex-shrink-0"
      style={{ background: 'linear-gradient(135deg,var(--kr-navy-700),var(--kr-navy-900))' }}
      aria-hidden="true"
    >
      {getInitials(name)}
    </div>
  );
  const subtitleBits = [];
  if (agent.npn) subtitleBits.push(`NPN ${agent.npn}`);
  if (agent.agency_name) subtitleBits.push(agent.agency_name);

  return (
    <DataCard
      title={name}
      subtitle={subtitleBits.join(' · ') || '—'}
      avatar={avatar}
      status={badge ? <StatusBadge variant={badge.variant} icon={BadgeIcon}>{badge.label}</StatusBadge> : null}
      onClick={() => onOpen(agent.npn)}
      testId={`onboarding-agent-card-${agent.npn}`}
    >
      <ChecklistBar checklist={agent.checklist} />
      <div className="mt-4 pt-3 grid grid-cols-2 gap-3 text-xs border-t" style={{ borderColor: 'var(--kr-border)', color: 'var(--kr-muted)' }}>
        <div>
          <div className="text-[10px] uppercase tracking-wider">Joined</div>
          <div className="mt-0.5 font-medium" style={{ color: 'var(--kr-navy-800)' }}>{relativeTime(agent.created_at)}</div>
        </div>
        <div>
          <div className="text-[10px] uppercase tracking-wider">Source</div>
          <div className="mt-0.5 font-medium flex items-center gap-1" style={{ color: 'var(--kr-navy-800)' }}>
            {agent.onboarded_via === 'self_service_link'
              ? (<><Link2 size={11} /> Self-service</>)
              : (<><Users size={11} /> Added by staff</>)}
          </div>
        </div>
      </div>
      <div
        className="mt-3 w-full inline-flex items-center justify-center gap-1.5 rounded-lg py-2 text-sm font-medium text-white transition-colors group-hover:brightness-110 cursor-pointer"
        style={{ background: 'var(--kr-navy-900)' }}
        onClick={(e) => { e.stopPropagation(); onOpenProfile(agent.npn); }}
        data-testid={`onboarding-open-profile-${agent.npn}`}
      >
        Open onboarding profile <ArrowUpRight size={15} />
      </div>
    </DataCard>
  );
}

const INVITE_BADGE_ROSTER = {
  invited: { label: 'Invite sent', variant: 'warning', icon: Mail },
  opened: { label: 'Link opened', variant: 'info', icon: MailOpen },
  expired: { label: 'Invite expired', variant: 'danger', icon: AlertTriangle },
};

function InviteCard({ invite, onResend, onOpen }) {
  const badge = INVITE_BADGE_ROSTER[invite.invite_status] || INVITE_BADGE_ROSTER.invited;
  const BadgeIcon = badge.icon;
  const prospectName = `${invite.first_name || ''} ${invite.last_name || ''}`.trim();
  const total = invite.total_steps || 5;
  const reached = invite.furthest_step || (invite.opened_at ? 1 : 0);
  const pct = Math.round((Math.min(reached, total) / total) * 100);
  const hasProgress = reached > 0 && invite.invite_status !== 'completed';
  return (
    <DataCard
      title={prospectName || invite.email}
      subtitle={[invite.npn ? `NPN ${invite.npn}` : null, invite.agency_name].filter(Boolean).join(' · ') || '—'}
      avatar={(
        <div className="w-10 h-10 rounded-full flex items-center justify-center flex-shrink-0" style={{ background: '#fdf6e3' }}>
          <Mail size={17} style={{ color: '#b8862d' }} />
        </div>
      )}
      status={<StatusBadge variant={badge.variant} icon={BadgeIcon}>{badge.label}</StatusBadge>}
      onClick={() => onOpen(invite.link_id)}
      testId={`onboarding-invite-card-${invite.link_id}`}
    >
      <p className="text-sm text-slate-500">
        {prospectName ? <span className="text-slate-700 font-medium">{invite.email} · </span> : null}
        Invitation sent — waiting for the agent to complete their profile.
        {invite.from_prospect && <span className="ml-1 text-indigo-600 font-medium">(from a converted prospect)</span>}
      </p>

      {/* Step progress — how far the agent got before abandoning the form */}
      {hasProgress && (
        <div className="mt-3" data-testid={`onboarding-invite-progress-${invite.link_id}`}>
          <div className="flex items-center justify-between mb-1">
            <span className="text-[11px] font-semibold text-slate-600">
              Stopped at step {Math.min(reached, total)} of {total}
              {invite.furthest_step_label ? `: ${invite.furthest_step_label}` : ''}
            </span>
            <span className="text-[10px] text-slate-400">
              {invite.last_step_at ? relativeTime(invite.last_step_at) : (invite.opened_at ? relativeTime(invite.opened_at) : '')}
            </span>
          </div>
          <div className="h-1.5 rounded-full bg-slate-100 overflow-hidden">
            <div className="h-full rounded-full transition-all" style={{ width: `${pct}%`, background: 'linear-gradient(90deg,var(--kr-navy-700),var(--kr-navy-900))' }} />
          </div>
        </div>
      )}

      <div className="mt-3 pt-3 grid grid-cols-2 gap-3 text-xs border-t" style={{ borderColor: 'var(--kr-border)', color: 'var(--kr-muted)' }}>
        <div>
          <div className="text-[10px] uppercase tracking-wider">Sent</div>
          <div className="mt-0.5 font-medium" style={{ color: 'var(--kr-navy-800)' }}>{relativeTime(invite.created_at)}</div>
        </div>
        <div>
          <div className="text-[10px] uppercase tracking-wider">Opened</div>
          <div className="mt-0.5 font-medium" style={{ color: 'var(--kr-navy-800)' }}>{invite.opened_at ? relativeTime(invite.opened_at) : 'Not yet'}</div>
        </div>
      </div>
      <button
        type="button"
        className="mt-3 w-full inline-flex items-center justify-center gap-1.5 rounded-lg py-2 text-sm font-medium transition-colors border"
        style={{ borderColor: 'var(--kr-border)', color: 'var(--kr-navy-900)' }}
        onClick={(e) => { e.stopPropagation(); onResend(invite.link_id); }}
        data-testid={`onboarding-invite-resend-${invite.link_id}`}
      >
        <Send size={14} /> Resend invite
      </button>
    </DataCard>
  );
}

export default function OnboardingWorkflowsPage() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const isKR = isKRRole(user);
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [drawerTarget, setDrawerTarget] = useState(null);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [applications, setApplications] = useState([]);
  const [appDrawerOpen, setAppDrawerOpen] = useState(false);
  const [appTarget, setAppTarget] = useState(null);

  const openApplication = (id) => { setAppTarget(id); setAppDrawerOpen(true); };

  const openDrawer = (t) => { setDrawerTarget(t); setDrawerOpen(true); };

  useEffect(() => {
    if (!isKR) { setLoading(false); return; }
    load();
    const id = setInterval(load, 30000);
    return () => clearInterval(id);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isKR]);

  const load = async () => {
    try {
      const [roster, apps] = await Promise.all([
        agentOnboardingAPI.roster(),
        agentApplicationAPI.list({ status: 'pending' }).catch(() => ({ data: { items: [] } })),
      ]);
      setItems(Array.isArray(roster.data.items) ? roster.data.items : []);
      setApplications(Array.isArray(apps.data.items) ? apps.data.items : []);
    } catch (e) {
      toast.error('Failed to load onboarding roster');
    } finally {
      setLoading(false);
    }
  };

  const openProfile = (npn) => navigate(`/agents/${npn}?tab=onboarding`);

  const resendInvite = async (linkId) => {
    try {
      const { data } = await agentOnboardingAPI.resendLink(linkId);
      toast.success(data.email_sent ? `Invite re-sent to ${data.email}` : 'Fresh link ready');
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Could not resend the invite');
    }
  };

  const filtered = useMemo(() => {
    if (!search.trim()) return items;
    const q = search.trim().toLowerCase();
    return items.filter((a) =>
      (`${a.first_name || ''} ${a.last_name || ''}`).toLowerCase().includes(q)
      || (a.email || '').toLowerCase().includes(q)
      || (a.npn || '').toLowerCase().includes(q));
  }, [items, search]);

  const counts = useMemo(() => {
    const agents = items.filter((a) => a.kind !== 'invite');
    const invites = items.filter((a) => a.kind === 'invite');
    return {
      total: agents.length,
      ready: agents.filter((a) => a.checklist?.all_done).length,
      awaiting: invites.length,
      selfService: agents.filter((a) => a.onboarded_via === 'self_service_link').length,
    };
  }, [items]);

  if (!isKR) {
    return (
      <PageShell
        title="Agent Onboarding"
        description="Every agent currently being onboarded."
        breadcrumbs={[{ label: 'Dashboard', href: '/dashboard' }, { label: 'Agent Onboarding' }]}
      >
        <Alert data-testid="onboarding-access-denied">
          <AlertTriangle className="h-4 w-4" />
          <AlertDescription>Only KR administrators can view agent onboarding.</AlertDescription>
        </Alert>
      </PageShell>
    );
  }

  return (
    <PageShell
      title="Agent Onboarding"
      description="Every agent currently in onboarding — whether they joined through a self-service link or were added by staff. Work each one from their profile, then graduate them to Active."
      breadcrumbs={[
        { label: 'Dashboard', href: '/dashboard' },
        { label: 'Growth' },
        { label: 'Agent Onboarding' },
      ]}
      actions={
        <div className="flex items-center gap-2">
          <div className="relative">
            <Search className="pointer-events-none absolute left-2.5 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
            <Input
              className="w-64 pl-8 kr-focus"
              placeholder="Search by name, NPN, email"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              data-testid="onboarding-search-input"
            />
          </div>
          <button type="button" className="kr-btn-ghost" onClick={load} data-testid="onboarding-refresh-button">
            <ArrowRight className="mr-1 h-4 w-4 rotate-180" />
            Refresh
          </button>
        </div>
      }
    >
      <div className="space-y-6">
        <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
          <StatCard label="In onboarding" value={counts.total} tone="brand" icon={Workflow} testId="onboarding-count-total" />
          <StatCard label="Ready to activate" value={counts.ready} tone="success" icon={ShieldCheck} testId="onboarding-count-ready" />
          <StatCard label="Awaiting profile" value={counts.awaiting} tone="warning" icon={Clock} testId="onboarding-count-awaiting" />
          <StatCard label="Applications to review" value={applications.length} tone="neutral" icon={UserPlus} testId="onboarding-count-applications" />
        </div>

        {applications.length > 0 && (
          <div className="rounded-xl border border-amber-200 bg-amber-50/60 p-4" data-testid="applications-review-section">
            <div className="mb-3 flex items-center gap-2">
              <UserPlus className="h-4 w-4 text-amber-700" />
              <h3 className="text-sm font-semibold text-amber-900">Public applications awaiting review ({applications.length})</h3>
            </div>
            <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-3">
              {applications.map((a) => (
                <button
                  type="button"
                  key={a.application_id}
                  onClick={() => openApplication(a.application_id)}
                  data-testid={`application-card-${a.application_id}`}
                  className="flex flex-col items-start rounded-lg border border-amber-200 bg-white px-4 py-3 text-left transition-shadow hover:shadow-md"
                >
                  <div className="flex w-full items-center justify-between">
                    <span className="font-semibold text-slate-800 truncate">{`${a.first_name || ''} ${a.last_name || ''}`.trim() || a.email}</span>
                    <ArrowUpRight className="h-4 w-4 text-amber-600 shrink-0" />
                  </div>
                  <span className="text-xs text-slate-500">NPN {a.npn}</span>
                  <div className="mt-2 flex flex-wrap gap-1">
                    {(a.category_labels || []).slice(0, 4).map((c) => (
                      <span key={c} className="rounded-full bg-slate-100 px-2 py-0.5 text-[10px] font-medium text-slate-600">{c}</span>
                    ))}
                  </div>
                </button>
              ))}
            </div>
          </div>
        )}


        {loading ? (
          <div className="flex h-40 items-center justify-center text-slate-500" data-testid="onboarding-loading">
            <Loader2 className="h-5 w-5 animate-spin" />
          </div>
        ) : filtered.length === 0 ? (
          <EmptyState
            icon={Workflow}
            title="No agents in onboarding"
            description="When an agent completes a self-service link or is added with 'onboarding' status, they'll appear here."
            testId="onboarding-empty-state"
          />
        ) : (
          <div className="grid grid-cols-1 gap-4 lg:grid-cols-2 xl:grid-cols-3">
            {filtered.map((a) => (
              a.kind === 'invite'
                ? <InviteCard key={a.link_id} invite={a} onResend={resendInvite} onOpen={(linkId) => openDrawer({ kind: 'invite', linkId })} />
                : <AgentCard key={a.npn} agent={a} onOpen={(npn) => openDrawer({ kind: 'agent', npn })} onOpenProfile={openProfile} />
            ))}
          </div>
        )}
      </div>

      <OnboardingDetailDrawer
        open={drawerOpen}
        onOpenChange={setDrawerOpen}
        target={drawerTarget}
        onChanged={load}
      />

      <ApplicationReviewDrawer
        open={appDrawerOpen}
        onOpenChange={setAppDrawerOpen}
        applicationId={appTarget}
        onChanged={load}
      />
    </PageShell>
  );
}
